import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import uk.co.danielturner.portfolio.components.layouts.MarkdownLayout as _uk_co_danielturner_portfolio_components_layouts_MarkdownLayout
import uk.co.danielturner.portfolio.components.layouts.PageLayout as _uk_co_danielturner_portfolio_components_layouts_PageLayout
import uk.co.danielturner.portfolio.pages.HomePage as _uk_co_danielturner_portfolio_pages_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Portfolio"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "uk.co.danielturner.portfolio.components.layouts.MarkdownLayout",
            parentLayoutId = "uk.co.danielturner.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                uk.co.danielturner.portfolio.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _uk_co_danielturner_portfolio_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "uk.co.danielturner.portfolio.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _uk_co_danielturner_portfolio_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "uk.co.danielturner.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                uk.co.danielturner.portfolio.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _uk_co_danielturner_portfolio_pages_HomePage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", uk.co.danielturner.portfolio.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", uk.co.danielturner.portfolio.SubheadlineTextStyle)
        ctx.theme.registerStyle("markdown", uk.co.danielturner.portfolio.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", uk.co.danielturner.portfolio.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("footer", uk.co.danielturner.portfolio.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", uk.co.danielturner.portfolio.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("hero-container", uk.co.danielturner.portfolio.pages.HeroContainerStyle)
        ctx.theme.registerStyle("text", uk.co.danielturner.portfolio.pages.TextStyle)
        ctx.theme.registerVariant("-circle", uk.co.danielturner.portfolio.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", uk.co.danielturner.portfolio.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", uk.co.danielturner.portfolio.components.sections.SideMenuSlideInAnim)
        uk.co.danielturner.portfolio.initColorMode(ctx)
        uk.co.danielturner.portfolio.initStyles(ctx)
        uk.co.danielturner.portfolio.initSiteStyles(ctx)
        uk.co.danielturner.portfolio.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        uk.co.danielturner.portfolio.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
